// `useReportPrintHost.ts` — the APPLICATION half of the isolated print host
// (story 4-128d; P06 §4.3, P10 §2.3, DOD G-33).
//
// The in-frame half is `src/report-artifact/printHostBridge.ts`; the contract
// between them is `src/report-artifact/printHostProtocol.ts`, which is the only
// file either side reads field names out of.
//
// ── What this hook is responsible for ──────────────────────────────────────
// Owning the channel, and nothing that can be seen. It mints the session and
// render ids, validates every inbound message on all four axes the protocol
// defines, debounces renders, drops late answers, and reports a plain
// React state object. `PagedReportPreview.tsx` renders that state; this file
// renders nothing.
//
// ── Why the artifact is PROBED before the frame is mounted ─────────────────
// `public/report-document/` is BUILD OUTPUT and gitignored (correctly — a
// committed bundle is a second, drifting copy of source). A checkout that has
// not run the artifact build therefore has no `/report-document/index.html`,
// and both hosts that serve this app answer a missing path the same way: with
// the SPA's own `index.html`. Mounting an iframe at that URL would silently
// boot a second copy of the entire application inside the preview — a
// multi-megabyte, router-mounting, API-calling failure that presents as "the
// preview is blank".
//
// So the manifest is fetched first and its `contract` checked. It is 4 kB,
// same-origin, and it is the ONE response that can tell "published" apart from
// "SPA fallback" without executing anything. A failure names the build command
// instead of showing an empty box — the same standard `check-report-
// artifact.mjs` applies at the gate, applied here at the point of use.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ReportContent } from '../../../lib/api/reports.gen';
import {
  PRINT_HOST_PROTOCOL,
  buildPrintFrameUrl,
  readFrameMessage,
  type PrintHostIdentity,
  type PrintHostMessage,
  type PrintHostPagination,
} from '../../../report-artifact/printHostProtocol';

/**
 * Where the web publication lives. `frontend/scripts/report-artifact.mjs`
 * (`PUBLICATIONS.web`) writes it to `frontend/public/report-document/`, Vite
 * copies `public/` verbatim into `dist/`, and `dist/` is what the Cloudflare
 * Pages deploy serves — so this one path is correct in dev and in production
 * with no environment switch.
 *
 * A different ORIGIN (the R2/CDN topologies `report-artifact/README.md` §4
 * records as options for `4-128e`) is the one thing that would have to be
 * parameterised here. The message protocol already supports it — every
 * validation is written against `frameOrigin` rather than against
 * "same-origin" — so that change is a URL, not a redesign.
 */
export const REPORT_ARTIFACT_URL = '/report-document/index.html';
const REPORT_ARTIFACT_MANIFEST_URL = '/report-document/manifest.json';
const ARTIFACT_CONTRACT = 'sitewise.report-artifact/1';

/**
 * The same debounce the pre-4-128d host applied, for the same measured reason:
 * a Paged.js pass measures real box heights page by page, and `content`
 * changes identity on every keystroke in the Style/Content tabs.
 */
const RENDER_DEBOUNCE_MS = 250;

/** How long a mounted, reachable frame may take to announce itself. */
const READY_TIMEOUT_MS = 15_000;

export type ReportPrintHostStatus = 'probing' | 'loading' | 'ready' | 'error';

export interface ReportPrintHostState {
  status: ReportPrintHostStatus;
  /** Set once the probe succeeds; until then no frame is mounted at all. */
  frameUrl: string | null;
  /** Which artifact the frame reported loading — the 4-128c freshness contract, app-side. */
  identity: PrintHostIdentity | null;
  pagination: PrintHostPagination | null;
  /** Measured inside the frame, so the host can size it to its content. */
  contentHeight: number;
  error: string | null;
}

/** The imperative surface `TemplateEditorPage`'s outline navigation needs. */
export interface ReportPrintHostHandle {
  /**
   * Bring a section anchor into view.
   *
   * Two halves, split exactly where P06 §4.3 splits them: the frame RESOLVES
   * the anchor (only it can see the element) and this host SCROLLS (navigation
   * is host chrome, and only the embedding page can move its own scroll
   * container). So the round trip is asynchronous.
   *
   * Returns whether the request was DELIVERED, not whether the section exists.
   * `false` means there is no live print host — web preview mode, or a frame
   * that has not announced itself yet — which is the caller's signal to fall
   * back to its own same-document `scrollIntoView`.
   */
  scrollToSection(sectionKey: string): boolean;
}

/**
 * The scroll container the frame actually sits in. Report Studio's canvas is
 * not the window: `TemplateEditorWorkspace` lays the editor out inside a fixed
 * shell with its own scrolling main column, so `window.scrollTo` moves
 * nothing. Walking up for the first genuinely scrollable ancestor finds it
 * without this module having to know the shell's DOM — and falls back to the
 * document's own scrolling element for any consumer whose page does scroll.
 */
function scrollParentOf(element: HTMLElement): Element {
  let node: HTMLElement | null = element.parentElement;
  while (node) {
    const overflowY = window.getComputedStyle(node).overflowY;
    if ((overflowY === 'auto' || overflowY === 'scroll' || overflowY === 'overlay')
      && node.scrollHeight > node.clientHeight + 1) {
      return node;
    }
    node = node.parentElement;
  }
  return document.scrollingElement ?? document.documentElement;
}

function mintId(): string {
  // `crypto.randomUUID` needs a secure context. `localhost` and HTTPS are
  // secure; a LAN-IP dev URL (`http://192.168.x.x:5173`) is not, and this is
  // an in-page correlation token rather than a credential, so a plain random
  // fallback is honest here instead of a crash.
  const cryptoRef = globalThis.crypto;
  if (cryptoRef && typeof cryptoRef.randomUUID === 'function') return cryptoRef.randomUUID();
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
}

export interface UseReportPrintHostOptions {
  content: ReportContent;
  selectedSectionKey: string | null;
  onSelectSection?: ((sectionKey: string) => void) | undefined;
}

export interface UseReportPrintHostResult extends ReportPrintHostState {
  frameRef: React.RefObject<HTMLIFrameElement>;
  handle: ReportPrintHostHandle;
}

export function useReportPrintHost({
  content,
  selectedSectionKey,
  onSelectSection,
}: UseReportPrintHostOptions): UseReportPrintHostResult {
  const frameRef = useRef<HTMLIFrameElement>(null);
  // Minted ONCE per mount. Carried in the frame URL, echoed by every frame→host
  // message, and checked on every one of them — so a frame that is being
  // replaced cannot have its late messages mistaken for the new frame's.
  const sessionRef = useRef<string>('');
  if (!sessionRef.current) sessionRef.current = mintId();

  const frameUrlBase = useMemo(
    () => buildPrintFrameUrl(REPORT_ARTIFACT_URL, sessionRef.current, window.location.origin),
    []
  );
  const frameOrigin = useMemo(() => new URL(frameUrlBase).origin, [frameUrlBase]);

  const [state, setState] = useState<ReportPrintHostState>({
    status: 'probing',
    frameUrl: null,
    identity: null,
    pagination: null,
    contentHeight: 0,
    error: null,
  });

  // Refs, not effect dependencies: re-running the (expensive, debounced)
  // pagination pass because the SELECTION or the handler identity changed is
  // exactly what the pre-4-128d host refused to do, and the whole reason
  // selection has its own message.
  const onSelectSectionRef = useRef(onSelectSection);
  onSelectSectionRef.current = onSelectSection;
  const selectedSectionKeyRef = useRef(selectedSectionKey);
  selectedSectionKeyRef.current = selectedSectionKey;

  const readyRef = useRef(false);
  const renderIdRef = useRef<string | null>(null);

  const post = useCallback(
    (message: PrintHostMessage): boolean => {
      const frame = frameRef.current?.contentWindow;
      if (!frame) return false;
      // Explicit target origin, never '*'. If the frame has navigated
      // somewhere else, the browser drops the message rather than handing
      // report content to whatever is there now.
      frame.postMessage(message, frameOrigin);
      return true;
    },
    [frameOrigin]
  );

  // ── 1. probe the publication, then mount the frame ────────────────────────
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const response = await fetch(REPORT_ARTIFACT_MANIFEST_URL, { cache: 'no-store' });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const manifest = (await response.json()) as { contract?: string };
        if (manifest.contract !== ARTIFACT_CONTRACT) {
          throw new Error(`served a '${manifest.contract ?? 'unknown'}' document, not '${ARTIFACT_CONTRACT}'`);
        }
        if (cancelled) return;
        setState((prev) => ({ ...prev, status: 'loading', frameUrl: frameUrlBase }));
      } catch (error) {
        if (cancelled) return;
        // Build/deploy diagnostic, not a G-27 API refusal — the raw cause is
        // the detail that makes it actionable, and the recovery command is
        // named rather than implied.
        // eslint-disable-next-line no-restricted-syntax
        const cause = error instanceof Error ? error.message : String(error);
        setState((prev) => ({
          ...prev,
          status: 'error',
          error:
            `The shared report document artifact is not published at ${REPORT_ARTIFACT_URL} (${cause}). ` +
            'Build it with `make report-artifact` (or `cd frontend && yarn build:report-artifact`) and reload.',
        }));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [frameUrlBase]);

  // ── 2. the channel ────────────────────────────────────────────────────────
  // Attached BEFORE any frame exists, so the frame's `ready` can never arrive
  // before there is a listener for it.
  useEffect(() => {
    const onMessage = (event: MessageEvent): void => {
      const message = readFrameMessage(event, {
        session: sessionRef.current,
        frameOrigin,
        frameWindow: frameRef.current?.contentWindow ?? null,
      });
      if (!message) return;
      switch (message.type) {
        case 'ready':
          readyRef.current = true;
          setState((prev) => ({ ...prev, identity: message.identity }));
          return;
        case 'rendered':
          // The render-id gate. A pass that answers an input revision the
          // editor has already typed past is dropped, never displayed.
          if (message.renderId !== renderIdRef.current) return;
          setState((prev) => ({
            ...prev,
            status: 'ready',
            error: null,
            pagination: message.pagination,
            contentHeight: message.contentHeight,
          }));
          return;
        case 'resize':
          if (message.renderId !== renderIdRef.current) return;
          setState((prev) =>
            prev.contentHeight === message.contentHeight ? prev : { ...prev, contentHeight: message.contentHeight }
          );
          return;
        case 'select':
          if (message.renderId !== renderIdRef.current) return;
          onSelectSectionRef.current?.(message.sectionKey);
          return;
        case 'anchor': {
          if (message.renderId !== renderIdRef.current) return;
          // A miss does nothing, exactly as the pre-4-128d
          // `document.querySelector(...)?.scrollIntoView()` did.
          if (message.offset === null) return;
          const frame = frameRef.current;
          if (!frame) return;
          const scroller = scrollParentOf(frame);
          // RELATIVE, so no coordinate space has to be reconstructed: the
          // anchor's distance from the top of the frame's document, plus the
          // frame's own distance from the top of the scroller's viewport, is
          // exactly how far this scroller has to move to put the section at
          // the top of the reader's view — the same `block: 'start'` result
          // the same outline click produced before the boundary existed.
          const scrollerTop =
            scroller === document.scrollingElement || scroller === document.documentElement
              ? 0
              : scroller.getBoundingClientRect().top;
          const delta = frame.getBoundingClientRect().top + message.offset - scrollerTop;
          scroller.scrollBy({ top: delta, behavior: 'smooth' });
          return;
        }
        case 'error':
          if (message.renderId !== null && message.renderId !== renderIdRef.current) return;
          setState((prev) => ({ ...prev, status: 'error', error: message.message }));
          return;
      }
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [frameOrigin]);

  // ── 3. render, debounced, once the frame has announced itself ─────────────
  useEffect(() => {
    if (state.frameUrl === null) return undefined;
    let tick = 0;
    const started = Date.now();

    const send = (): void => {
      const renderId = mintId();
      renderIdRef.current = renderId;
      setState((prev) => (prev.status === 'error' ? prev : { ...prev, status: 'loading' }));
      post({
        protocol: PRINT_HOST_PROTOCOL,
        session: sessionRef.current,
        renderId,
        type: 'render',
        content,
        variant: 'print',
        // Story 4-111 — the TOC rows render with EMPTY, hooked page slots and
        // the frame's own pagination pass fills them in. Unchanged by the
        // boundary; it just happens on the other side of it now.
        pendingTocPageNumbers: true,
        selectedSectionKey: selectedSectionKeyRef.current,
        selectable: Boolean(onSelectSectionRef.current),
      });
    };

    // The debounce first — identical in value and reason to the pre-4-128d
    // host's — then a wait on the frame's own `ready` MESSAGE. Deliberately
    // not the iframe's `load` event: `load` fires when the document is parsed,
    // which is strictly earlier than "the module evaluated, the manifest was
    // fetched and the bridge installed". P06 §4.3: "A delay or `networkidle0`
    // alone is not readiness." The deadline below exists only so a frame that
    // never answers becomes a NAMED failure instead of a permanent skeleton.
    const debounce = window.setTimeout(() => {
      if (readyRef.current) {
        send();
        return;
      }
      tick = window.setInterval(() => {
        if (readyRef.current) {
          window.clearInterval(tick);
          send();
          return;
        }
        if (Date.now() - started > READY_TIMEOUT_MS) {
          window.clearInterval(tick);
          setState((prev) => ({
            ...prev,
            status: 'error',
            error:
              `The isolated print document at ${REPORT_ARTIFACT_URL} loaded but never announced itself on ` +
              `'${PRINT_HOST_PROTOCOL}'. It may be a stale publication — rebuild with \`make report-artifact\`.`,
          }));
        }
      }, 50);
    }, RENDER_DEBOUNCE_MS);

    return () => {
      window.clearTimeout(debounce);
      if (tick) window.clearInterval(tick);
    };
    // `content` changes identity on every editor keystroke — re-rendering on
    // every change is the whole point of a LIVE preview.
  }, [content, state.frameUrl, post]);

  // ── 4. selection, without a re-layout ─────────────────────────────────────
  useEffect(() => {
    const renderId = renderIdRef.current;
    if (!renderId || state.status !== 'ready') return;
    post({
      protocol: PRINT_HOST_PROTOCOL,
      session: sessionRef.current,
      renderId,
      type: 'select',
      sectionKey: selectedSectionKey,
    });
  }, [selectedSectionKey, state.status, state.pagination, post]);

  const handle = useMemo<ReportPrintHostHandle>(
    () => ({
      scrollToSection(sectionKey: string): boolean {
        const renderId = renderIdRef.current;
        if (!renderId || !readyRef.current) return false;
        return post({
          protocol: PRINT_HOST_PROTOCOL,
          session: sessionRef.current,
          renderId,
          type: 'resolveAnchor',
          sectionKey,
        });
      },
    }),
    [post]
  );

  return { ...state, frameRef, handle };
}
